import {gql} from '@apollo/client';

/* ---------- Categories ---------- */

export const GET_HELPDESK_CATEGORIES = gql`
  query GetHelpdeskCategories($input: HelpdeskCategoryFilterInput!) {
    getHelpdeskCategories(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskCategories {
        message
        data {
          id
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
          createdAt
          updatedAt
        }
        pagination {
          page
          limit
          pages
        }
      }
    }
  }
`;

export const CREATE_HELPDESK_CATEGORY = gql`
  mutation CreateHelpdeskCategory($input: CreateHelpdeskCategoryInput!) {
    createHelpdeskCategory(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskCategory {
        message
        data {
          id
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
        }
      }
    }
  }
`;

export const UPDATE_HELPDESK_CATEGORY = gql`
  mutation UpdateHelpdeskCategory($input: UpdateHelpdeskCategoryInput!) {
    updateHelpdeskCategory(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskCategory {
        message
        data {
          id
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
        }
      }
    }
  }
`;

export const DELETE_HELPDESK_CATEGORY = gql`
  mutation DeleteHelpdeskCategory($input: DeleteHelpdeskCategoryInput!) {
    deleteHelpdeskCategory(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskDeleted {
        message
        data
      }
    }
  }
`;

/* ---------- Features ---------- */

export const GET_HELPDESK_FEATURES = gql`
  query GetHelpdeskFeatures($input: HelpdeskFeatureFilterInput!) {
    getHelpdeskFeatures(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeatures {
        message
        data {
          id
          categoryId
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
          createdAt
          updatedAt
        }
        pagination {
          page
          limit
          pages
        }
      }
    }
  }
`;

export const CREATE_HELPDESK_FEATURE = gql`
  mutation CreateHelpdeskFeature($input: CreateHelpdeskFeatureInput!) {
    createHelpdeskFeature(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeature {
        message
        data {
          id
          categoryId
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
        }
      }
    }
  }
`;

export const UPDATE_HELPDESK_FEATURE = gql`
  mutation UpdateHelpdeskFeature($input: UpdateHelpdeskFeatureInput!) {
    updateHelpdeskFeature(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeature {
        message
        data {
          id
          categoryId
          name
          iconUrl
          sortOrder
          isActive
          language
          availableToAnonymous
        }
      }
    }
  }
`;

export const DELETE_HELPDESK_FEATURE = gql`
  mutation DeleteHelpdeskFeature($input: DeleteHelpdeskFeatureInput!) {
    deleteHelpdeskFeature(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskDeleted {
        message
        data
      }
    }
  }
`;

/* ---------- Issues ---------- */

export const GET_HELPDESK_FEATURE_ISSUES = gql`
  query GetHelpdeskFeatureIssues($input: HelpdeskFeatureIssueFilterInput!) {
    getHelpdeskFeatureIssues(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeatureIssues {
        message
        data {
          id
          featureId
          title
          stepTemplate
          skillId
          faqPostIds
          sortOrder
          isActive
          language
          availableToAnonymous
          fallthrough
          contextConfig {
            key
            type
            dependsOn
            resolver
            payload
          }
          faqs {
            postid
            question
            answer
            language
          }
          createdAt
          updatedAt
        }
        pagination {
          page
          limit
          pages
        }
      }
    }
  }
`;

export const CREATE_HELPDESK_FEATURE_ISSUE = gql`
  mutation CreateHelpdeskFeatureIssue(
    $input: CreateHelpdeskFeatureIssueInput!
  ) {
    createHelpdeskFeatureIssue(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeatureIssue {
        message
        data {
          id
          featureId
          title
          stepTemplate
          skillId
          faqPostIds
          sortOrder
          isActive
          language
          availableToAnonymous
          fallthrough
        }
      }
    }
  }
`;

export const UPDATE_HELPDESK_FEATURE_ISSUE = gql`
  mutation UpdateHelpdeskFeatureIssue(
    $input: UpdateHelpdeskFeatureIssueInput!
  ) {
    updateHelpdeskFeatureIssue(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFeatureIssue {
        message
        data {
          id
          featureId
          title
          stepTemplate
          skillId
          faqPostIds
          sortOrder
          isActive
          language
          availableToAnonymous
          fallthrough
        }
      }
    }
  }
`;

export const DELETE_HELPDESK_FEATURE_ISSUE = gql`
  mutation DeleteHelpdeskFeatureIssue(
    $input: DeleteHelpdeskFeatureIssueInput!
  ) {
    deleteHelpdeskFeatureIssue(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskDeleted {
        message
        data
      }
    }
  }
`;

export const GET_CONTEXT_RESOLVER_CATALOG = gql`
  query GetContextResolverCatalog {
    getContextResolverCatalog {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithContextResolverCatalog {
        message
        data {
          resolver
          typename
          applicableKeys
          stepTypes
          description
          defaultLimit
          userScoped
          supportedFilters {
            key
            type
            enumValues
            description
          }
        }
      }
    }
  }
`;

export const GET_HELPDESK_FAQS = gql`
  query GetHelpdeskFAQs($input: HelpdeskFAQFilterInput!) {
    getHelpdeskFAQs(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFAQs {
        message
        data {
          postid
          question
          answer
          language
        }
        pagination {
          page
          limit
          pages
        }
      }
    }
  }
`;

export const ATTACH_HELPDESK_FAQS = gql`
  mutation AttachHelpdeskFAQs($input: AttachHelpdeskFAQsInput!) {
    attachHelpdeskFAQs(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFAQs {
        message
        data {
          postid
          question
          answer
          language
        }
      }
    }
  }
`;

export const DETACH_HELPDESK_FAQS = gql`
  mutation DetachHelpdeskFAQs($input: DetachHelpdeskFAQsInput!) {
    detachHelpdeskFAQs(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskFAQs {
        message
        data {
          postid
          question
          answer
          language
        }
      }
    }
  }
`;
